/**
 * Marks the "Create a new workout" tutorial step (step 3) as completed.
 * This should be called when:
 * - Adding the first exercise to a workout
 * - Adding a workout from the library
 *
 * @param {Object} coach - The coach object (must have onboardingShowBlock property)
 * @param {Function} showToast - Toast function to show completion message
 * @returns {boolean} - Returns true if the step was marked as completed, false otherwise
 */
export const markTutorialStepCompleted = (coach, showToast) => {
  if (!coach || !coach.onboardingShowBlock) {
    return false;
  }

  coach.setProperty("onboardingShowBlock", false);
  coach.updateCoachPublicProfile({
    onboarding_show_block: false,
  });

  showToast("step2Completed-string", {
    type: "info",
    description: "step2CompletedDescription-string",
  });

  return true;
};

/**
 * Controls when the empty workout "Add exercise" CTA should be highlighted
 * for the onboarding tutorial.
 *
 * @param {Object} params
 * @param {Object} params.coach
 * @param {boolean} params.hasExercises
 * @param {boolean} params.isAddingExercise
 * @returns {boolean}
 */
export const shouldShowProminentTutorialAddExercise = ({
  coach,
  hasExercises,
  isAddingExercise,
}) => {
  if (coach?.onboardingShowBlock !== true) return false;
  if (hasExercises) return false;
  if (isAddingExercise) return false;
  return true;
};

/**
 * Returns getting started tutorial steps in display order.
 * Step numbering matches the UI labels in GettingStartedView.
 *
 * @param {Object} coach
 * @returns {Array<{
 *   number: number,
 *   titleKey: string,
 *   descriptionKey: string,
 *   ctaKey: string | null,
 *   actionType: "block" | "app" | "client" | null,
 *   isCompleted: boolean
 * }>}
 */
export const getTutorialSteps = (coach) => [
  {
    number: 1,
    titleKey: "tutorialStep1-string",
    descriptionKey: "tutorialStep1text1-string",
    ctaKey: null,
    actionType: null,
    isCompleted: true,
  },
  {
    number: 2,
    titleKey: "tutorialStep3-string",
    descriptionKey: "tutorialStep3text1-string",
    ctaKey: "tutorialStep3text2-string",
    actionType: "block",
    isCompleted: !coach?.onboardingShowBlock,
  },
  {
    number: 3,
    titleKey: "tutorialStep2-string",
    descriptionKey: "tutorialStep2text1-string",
    ctaKey: "tutorialStep2text2-string",
    actionType: "app",
    isCompleted: !coach?.onboardingShowAthleteApp,
  },
  {
    number: 4,
    titleKey: "tutorialStep4-string",
    descriptionKey: "tutorialStep4text1-string",
    ctaKey: "tutorialStep4text2-string",
    actionType: "client",
    isCompleted: !coach?.onboardingShowInviteClient,
  },
];

/**
 * Returns the next incomplete, actionable tutorial step.
 *
 * @param {Object} coach
 * @returns {Object | null}
 */
export const getNextIncompleteTutorialStep = (coach) =>
  getTutorialSteps(coach).find(
    (step) => step.actionType && step.isCompleted === false
  ) || null;
